import { useNavigate } from "@tanstack/react-router";
import { AlertTriangle, Check, KeyRound, Plus, Trash2, X } from "lucide-react";
import { useState } from "react";
import {
  useAddEnvVar,
  useDeleteDesk,
  useDeleteEnvVar,
  useEnvVars,
} from "@/api/hooks";
import type { DeskRow } from "@/api/types";
import { useNotificationPrefs } from "@/notifications/app-activity-provider";

interface SettingsPanelProps {
  desk: DeskRow;
  onClose?: () => void;
}

/**
 * Desk-level settings: env vars (API keys / runtime config the sprite passes
 * to the agent) and the destructive "delete desk" action. System prompt is
 * read-only here — changing it on an existing desk doesn't take effect until
 * the sprite is redeployed, so we just display it for context.
 */
export function SettingsPanel({ desk, onClose }: SettingsPanelProps) {
  return (
    <div
      className="h-full flex flex-col"
      style={{ background: "var(--surface)" }}
    >
      <div
        className="flex items-center justify-between px-3 py-2 border-b shrink-0"
        style={{ borderColor: "var(--blt)" }}
      >
        <span
          className="text-[11px] font-medium uppercase tracking-wider"
          style={{
            color: "var(--t3)",
            fontFamily: "var(--font-mono)",
            letterSpacing: "0.08em",
          }}
        >
          Settings
        </span>
        {onClose && (
          <button
            type="button"
            onClick={onClose}
            className="p-1 rounded hover:bg-[var(--hover)]"
            style={{ color: "var(--t3)" }}
            title="Close settings"
          >
            <X size={13} />
          </button>
        )}
      </div>

      <div className="flex-1 overflow-y-auto">
        <DeskMetaSection desk={desk} />
        <NotificationSection />
        <EnvVarsSection deskId={desk.id} />
        <DangerSection desk={desk} />
      </div>
    </div>
  );
}

function DeskMetaSection({ desk }: { desk: DeskRow }) {
  return (
    <div
      className="px-3 py-3 border-b space-y-2"
      style={{ borderColor: "var(--blt)" }}
    >
      <SectionHeading>About</SectionHeading>
      <MetaRow label="Name" value={desk.name} />
      {desk.sprite_name && (
        <MetaRow label="Sprite" value={desk.sprite_name} mono />
      )}
      <MetaRow
        label="Created"
        value={new Date(
          desk.created_at + (desk.created_at.includes("Z") ? "" : "Z"),
        ).toLocaleString()}
      />
      {desk.total_spend_usd > 0 && (
        <MetaRow
          label="Spend"
          value={`$${desk.total_spend_usd.toFixed(3)}`}
          mono
        />
      )}
      {desk.system_prompt && (
        <div className="pt-2">
          <SectionHeading small>System prompt</SectionHeading>
          <p
            className="text-[11px] mt-1 whitespace-pre-wrap rounded p-2"
            style={{
              background: "var(--white)",
              border: "1px solid var(--blt)",
              color: "var(--t2)",
              fontFamily: "var(--font-body)",
              lineHeight: 1.5,
              maxHeight: 160,
              overflowY: "auto",
            }}
          >
            {desk.system_prompt}
          </p>
        </div>
      )}
    </div>
  );
}

function MetaRow({
  label,
  value,
  mono,
}: {
  label: string;
  value: string;
  mono?: boolean;
}) {
  return (
    <div className="flex items-baseline gap-2 text-[11px]">
      <span
        className="w-14 shrink-0 uppercase tracking-wider"
        style={{
          color: "var(--t4)",
          fontFamily: "var(--font-mono)",
          letterSpacing: "0.06em",
        }}
      >
        {label}
      </span>
      <span
        className="truncate"
        style={{
          color: "var(--t1)",
          fontFamily: mono ? "var(--font-mono)" : "var(--font-body)",
        }}
        title={value}
      >
        {value}
      </span>
    </div>
  );
}

function SectionHeading({
  children,
  small,
}: {
  children: React.ReactNode;
  small?: boolean;
}) {
  return (
    <div
      className={small ? "text-[10px]" : "text-[11px]"}
      style={{
        color: "var(--t3)",
        fontFamily: "var(--font-mono)",
        fontWeight: 500,
        textTransform: "uppercase",
        letterSpacing: "0.08em",
      }}
    >
      {children}
    </div>
  );
}

function NotificationSection() {
  const { prefs, setDesktopEnabled, permission, requestPermission } =
    useNotificationPrefs();
  const isMac =
    typeof navigator !== "undefined" &&
    /Mac|iPhone|iPad|iPod/.test(navigator.platform);

  return (
    <div
      className="px-3 py-3 border-b space-y-2"
      style={{ borderColor: "var(--blt)" }}
    >
      <SectionHeading>Notifications</SectionHeading>
      <p className="text-[10px] leading-relaxed" style={{ color: "var(--t4)" }}>
        Browser-level alerts for completed agent tasks. Notifications appear
        when this tab is in the background.
      </p>

      <label className="flex items-start gap-2 text-[11px] cursor-pointer">
        <input
          type="checkbox"
          checked={prefs.desktop}
          onChange={(e) => setDesktopEnabled(e.target.checked)}
          className="mt-0.5"
        />
        <span style={{ color: "var(--t1)" }}>Enable desktop notifications</span>
      </label>

      {prefs.desktop &&
        permission !== "granted" &&
        permission !== "unsupported" && (
          <div className="space-y-1">
            <button
              type="button"
              onClick={() => void requestPermission()}
              className="text-[11px] px-2 py-1 rounded"
              style={{
                background:
                  "color-mix(in srgb, var(--accent) 10%, transparent)",
                color: "var(--accent)",
              }}
            >
              Allow browser notifications
            </button>
            <p className="text-[10px]" style={{ color: "var(--t4)" }}>
              Current permission: {permission}
            </p>
          </div>
        )}

      {permission === "denied" && (
        <p
          className="text-[10px] leading-relaxed"
          style={{ color: "var(--warm)" }}
        >
          Notifications are blocked in this browser. Re-enable them in site or
          browser settings.
        </p>
      )}

      {permission === "unsupported" && (
        <p
          className="text-[10px] leading-relaxed"
          style={{ color: "var(--warm)" }}
        >
          This browser does not support desktop notifications.
        </p>
      )}

      {isMac && (
        <div
          className="text-[10px] leading-relaxed rounded p-2 space-y-1"
          style={{
            color: "var(--t4)",
            background: "var(--white)",
            border: "1px solid var(--blt)",
          }}
        >
          <p style={{ color: "var(--t3)" }}>On macOS, also check:</p>
          <ul className="list-disc pl-4 space-y-0.5">
            <li>System Settings → Notifications → your browser</li>
            <li>Allow notifications is enabled</li>
            <li>Banner or alert style is enabled</li>
            <li>Focus / Do Not Disturb is off</li>
          </ul>
        </div>
      )}
    </div>
  );
}

function EnvVarsSection({ deskId }: { deskId: string }) {
  const { data, isLoading, error } = useEnvVars(deskId);
  const addEnv = useAddEnvVar(deskId);
  const deleteEnv = useDeleteEnvVar(deskId);
  const [adding, setAdding] = useState(false);
  const [key, setKey] = useState("");
  const [val, setVal] = useState("");
  const [addError, setAddError] = useState<string | null>(null);

  const submitAdd = async () => {
    const k = key.trim();
    if (!k) return;
    setAddError(null);
    try {
      await addEnv.mutateAsync({ key: k, value: val });
      setKey("");
      setVal("");
      setAdding(false);
    } catch (e) {
      setAddError(e instanceof Error ? e.message : "Failed");
    }
  };

  return (
    <div className="px-3 py-3 border-b" style={{ borderColor: "var(--blt)" }}>
      <div className="flex items-center gap-2 mb-2">
        <SectionHeading>Environment</SectionHeading>
        <div className="flex-1" />
        {!adding && (
          <button
            type="button"
            onClick={() => setAdding(true)}
            className="text-[11px] flex items-center gap-1 px-2 py-0.5 rounded"
            style={{
              background: "color-mix(in srgb, var(--accent) 10%, transparent)",
              color: "var(--accent)",
            }}
            title="Add env var"
          >
            <Plus size={10} />
            add
          </button>
        )}
      </div>
      <p
        className="text-[10px] mb-2 leading-relaxed"
        style={{ color: "var(--t4)" }}
      >
        Passed to every agent running on this desk. Values are write-only — we
        never display them back.
      </p>

      {adding && (
        <div
          className="rounded-md p-2 mb-2 space-y-1.5"
          style={{
            background: "var(--white)",
            border: "1px solid var(--border)",
          }}
        >
          <input
            type="text"
            value={key}
            onChange={(e) =>
              setKey(e.target.value.toUpperCase().replace(/[^A-Z0-9_]/g, ""))
            }
            placeholder="KEY_NAME"
            className="w-full text-[12px] px-2 py-1 rounded focus:outline-none"
            style={{
              background: "var(--surface)",
              border: "1px solid var(--blt)",
              fontFamily: "var(--font-mono)",
              color: "var(--t1)",
            }}
          />
          <input
            type="password"
            value={val}
            onChange={(e) => setVal(e.target.value)}
            placeholder="value"
            className="w-full text-[12px] px-2 py-1 rounded focus:outline-none"
            style={{
              background: "var(--surface)",
              border: "1px solid var(--blt)",
              fontFamily: "var(--font-mono)",
              color: "var(--t1)",
            }}
            onKeyDown={(e) => {
              if (e.key === "Enter") submitAdd();
              if (e.key === "Escape") setAdding(false);
            }}
          />
          {addError && (
            <p className="text-[11px]" style={{ color: "var(--err)" }}>
              {addError}
            </p>
          )}
          <div className="flex items-center gap-1">
            <button
              type="button"
              onClick={submitAdd}
              disabled={!key.trim() || addEnv.isPending}
              className="text-[11px] px-2 py-1 rounded flex items-center gap-1 disabled:opacity-40"
              style={{
                background: "var(--accent)",
                color: "var(--white)",
              }}
            >
              <Check size={10} />
              Save
            </button>
            <button
              type="button"
              onClick={() => {
                setAdding(false);
                setKey("");
                setVal("");
                setAddError(null);
              }}
              className="text-[11px] px-2 py-1 rounded"
              style={{ color: "var(--t3)" }}
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      {isLoading && (
        <p className="text-[11px]" style={{ color: "var(--t4)" }}>
          Loading…
        </p>
      )}
      {error && (
        <p className="text-[11px]" style={{ color: "var(--err)" }}>
          Couldn't load env vars
        </p>
      )}
      {data?.vars.length === 0 && !adding && (
        <p
          className="text-[11px]"
          style={{
            color: "var(--t4)",
            fontFamily: "var(--font-mono)",
          }}
        >
          no env vars set
        </p>
      )}
      {data && data.vars.length > 0 && (
        <ul className="space-y-1">
          {data.vars.map((k) => (
            <li
              key={k}
              className="flex items-center gap-2 px-2 py-1 rounded group"
              style={{ background: "var(--white)" }}
            >
              <KeyRound size={10} style={{ color: "var(--t4)" }} />
              <span
                className="flex-1 truncate text-[12px]"
                style={{
                  fontFamily: "var(--font-mono)",
                  color: "var(--t1)",
                }}
              >
                {k}
              </span>
              <button
                type="button"
                onClick={() => {
                  if (confirm(`Delete env var ${k}?`)) deleteEnv.mutate(k);
                }}
                className="p-0.5 rounded opacity-0 group-hover:opacity-100"
                style={{ color: "var(--t4)" }}
                title={`Delete ${k}`}
              >
                <Trash2 size={11} />
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function DangerSection({ desk }: { desk: DeskRow }) {
  const navigate = useNavigate();
  const deleteDesk = useDeleteDesk();
  const [armed, setArmed] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleDelete = async () => {
    setError(null);
    try {
      await deleteDesk.mutateAsync(desk.id);
      navigate({ to: "/" });
    } catch (e) {
      setError(e instanceof Error ? e.message : "Delete failed");
    }
  };

  return (
    <div className="px-3 py-3 space-y-2">
      <SectionHeading>Danger</SectionHeading>
      <p className="text-[11px] leading-relaxed" style={{ color: "var(--t4)" }}>
        Deletes the desk, its sprite, and every session on it. Files and
        conversation history will be gone — this can't be undone.
      </p>
      {error && (
        <div
          className="flex items-start gap-2 px-2 py-1.5 rounded"
          style={{
            background: "color-mix(in srgb, var(--err) 6%, transparent)",
          }}
        >
          <AlertTriangle
            size={11}
            style={{ color: "var(--err)", flexShrink: 0, marginTop: 2 }}
          />
          <p className="text-[11px]" style={{ color: "var(--err)" }}>
            {error}
          </p>
        </div>
      )}
      {!armed ? (
        <button
          type="button"
          onClick={() => setArmed(true)}
          className="text-[12px] px-2.5 py-1 rounded flex items-center gap-1.5"
          style={{
            background: "transparent",
            color: "var(--err)",
            border: "1px solid color-mix(in srgb, var(--err) 30%, transparent)",
          }}
        >
          <Trash2 size={12} />
          Delete desk
        </button>
      ) : (
        <div className="flex items-center gap-1.5">
          <button
            type="button"
            onClick={handleDelete}
            disabled={deleteDesk.isPending}
            className="text-[12px] px-2.5 py-1 rounded flex items-center gap-1.5 disabled:opacity-40"
            style={{
              background: "var(--err)",
              color: "var(--white)",
            }}
          >
            <Trash2 size={12} />
            {deleteDesk.isPending ? "Deleting…" : "Confirm delete"}
          </button>
          <button
            type="button"
            onClick={() => setArmed(false)}
            className="text-[12px] px-2.5 py-1 rounded"
            style={{ color: "var(--t3)" }}
          >
            Cancel
          </button>
        </div>
      )}
    </div>
  );
}
